import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthService } from '@app/services/auth.service';
import { SMELessonService } from '@app/services/sme-lesson.service';
import { TOCService } from '@app/services/toc.service';
import { lessonSlideService } from '@app/services/lesson-slide.service';
import { CommonApiService } from '@app/services/common-api.service';
import { lessonImageContentUrlCandidates } from '@app/utils/lesson-image-url';
import { promptQuizConfig, quizConfigFromLessonPreferences } from '@app/utils/quiz-config-prompt';
import { extractApiErrorMessage, confirmContinueAfterAiError } from '@app/utils/ai-error-prompt';
import { unwrapLessonRow, unwrapVioResponseData } from '@app/utils/vio-api-response';
import { triggerGeneratedLessonPptDownload } from '@app/utils/lesson-ppt-download';
import { parseTocNoteFromApi } from '@app/utils/toc-notes';
import { TocTree } from '@app/models/toc.model';
import { environment } from '@env/environment';
import { QuizConfig } from '@app/models/toc.model';
import {
  documentStrictMissingPageRefMessage,
  documentStrictSlideNeedsPageRef,
  isDocumentStrictLesson,
  isQuizTocSlide,
} from '@app/utils/primary-source-toc';
import { scrollToTocSlide, scrollToTocSlideWhenReady, tocSlideDomId } from '@app/utils/toc-slide-scroll';
import { mergeInsertedTocSlides } from '@app/utils/toc-insert-slides';

type ChatRole = 'system' | 'user' | 'assistant';

type ChatMode = 'pickLesson' | 'createLesson' | 'toc' | 'slide';

interface ChatMessage {
  role: ChatRole;
  text: string;
  ts: number;
}

@Component({
  selector: 'app-sme-chat',
  templateUrl: './sme-chat.component.html',
  styleUrls: ['./sme-chat.component.scss'],
})
export class SMEChatComponent implements OnInit {
  messages: ChatMessage[] = [];
  inputText = '';

  mode: ChatMode = 'pickLesson';

  userId!: number;
  lessons: any[] = [];
  loadingLessons = false;

  selectedLessonId: number | null = null;
  lessonMeta: any | null = null;

  toc: any | null = null;

  activeSlide: any | null = null;
  activeSlideEdit: any | null = null; // same shape expected by saveLessonSlide (slideElements)
  activeSlideId: string | null = null;
  activeSlideTitle: string | null = null;
  slidePreviewNote = 'Slide preview — approximate (not a PowerPoint render).';
  showSlideNotes = false;
  showElementNotes = false;
  previewExpanded = false;
  showAdvancedElementFields = false;

  // Delete confirmations
  confirmDelete: null | { kind: 'section' | 'slide'; sectionIndex: number; slideIndex?: number } = null;
  confirmDeleteElementIndex: number | null = null;
  imageLoadState: Record<string, 'ok' | 'error'> = {};
  /** Per element IMAGE: next candidate index after load error (legacy paths). */
  private lessonImgCandidateIdx: Record<string, number> = {};
  /** Cache-bust after regenerate image. */
  private imagePreviewBust: Record<string, number> = {};
  /** elementId currently regenerating. */
  regeneratingImageElementId: string | null = null;
  previewLayoutImgCandidates: string[] = [];
  private previewLayoutImgIdx = 0;
  tocDirty = false;
  slideDirty = false;

  /** Natural-language lesson creation via backend OpenAI */
  aiLessonChatActive = false;
  aiLessonPartial: any | null = null;
  lessonAiBusy = false;
  /** SSE / long backend work for TOC or slide (separate from AI lesson chat). */
  tocBusy = false;
  slideBusy = false;
  /** Slide currently being generated (TOC row highlight + sticky status). */
  generatingSlideId: string | null = null;
  generatingSlideTitle = '';
  generatingProgressMessage = '';
  tocProgressMessage = '';

  /** Inline lesson list under chat (checks unsaved work before opening) */
  showLessonPicker = false;

  /** '' = all; SME workflow filter passed to lessonsList */
  lessonLifecycleFilter: '' | 'IN_PROGRESS' | 'RELEASED' | 'ARCHIVED' = '';

  /** Workflow status for the open lesson (mirrors list row after pick / refresh). */
  selectedLessonLifecycleStatus: 'IN_PROGRESS' | 'RELEASED' | 'ARCHIVED' = 'IN_PROGRESS';
  private savedQuizDefaults: Partial<QuizConfig> = {};

  pptGenBusy = false;

  /** SME reference files (PDF/images) for AI grounding — managed in chat when a lesson is open. */
  references: any[] = [];
  refUploadError = '';
  refUploading = false;
  refRefreshOpenAiBusy = false;
  refUploadPrimary: 'Y' | 'N' = 'N';
  refUploadStatus = '';
  readonly refAllowedMimeTypes = [
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
  ];
  readonly refMaxBytes = 25 * 1024 * 1024;
  readonly refMaxMbLabel = '25 MB';

  selectedLessonContentMode = 'generic';

  /** License pool status for create-lesson gate */
  licenseStatus: { ok?: boolean; message?: string; available?: number; total?: number } | null = null;

  /** TOC slide object currently open in slide mode (same reference as in toc.sections) */
  private tocSlideBeingEdited: any | null = null;

  /** Last slide opened/generated — restore TOC scroll on return from slide mode. */
  lastTocSlideId: string | null = null;
  highlightedTocSlideId: string | null = null;
  readonly isDocumentStrictLesson = isDocumentStrictLesson;
  readonly tocSlideDomId = tocSlideDomId;

  @ViewChild('colDetails') colDetails?: ElementRef<HTMLElement>;

  /** Deep-link from dashboard / TOC editor after clear content. */
  private pendingOpenLessonId: number | null = null;
  private pendingGenerateToc = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private auth: AuthService,
    private lessonSvc: SMELessonService,
    private tocSvc: TOCService,
    private slideSvc: lessonSlideService,
    private commonAPI: CommonApiService
  ) {}

  goPowerUserMode() {
    if (!this.maybeSaveBeforeNavigate('lesson')) return;
    this.router.navigate(['/SME/dashboard']);
  }

  /** True while TOC, slide, or lesson AI generation is in flight. */
  get aiGenerationBusy(): boolean {
    return this.lessonAiBusy || this.tocBusy || this.slideBusy;
  }

  get remoteWorkBusy(): boolean {
    return this.aiGenerationBusy || this.pptGenBusy;
  }

  /** Primary line for sticky generation status bar (right panel). */
  generationStatusPrimary(): string {
    if (this.pptGenBusy) return 'Generating PowerPoint…';
    if (this.lessonAiBusy) return 'Waiting for AI (lesson)…';
    if (this.tocBusy) return 'Generating table of contents…';
    if (this.slideBusy) {
      const t = String(this.generatingSlideTitle || '').trim();
      return t ? `Generating: ${t}` : 'Generating slide content…';
    }
    return '';
  }

  /** Secondary SSE progress line under the primary status. */
  generationStatusDetail(): string {
    if (this.slideBusy) return String(this.generatingProgressMessage || '').trim();
    if (this.tocBusy) return String(this.tocProgressMessage || '').trim();
    return '';
  }

  isTocSlideGenerating(slideId: string | null | undefined): boolean {
    return this.slideBusy && !!this.generatingSlideId && this.generatingSlideId === slideId;
  }

  lifecycleLabel(code: string | undefined | null): string {
    const k = String(code || 'IN_PROGRESS').toUpperCase();
    const m: Record<string, string> = {
      IN_PROGRESS: 'In progress',
      RELEASED: 'Released',
      ARCHIVED: 'Archived',
    };
    return m[k] || k;
  }

  onLessonLifecycleFilterChange(): void {
    this.loadLessons();
  }

  ngOnInit(): void {
    this.userId = this.auth.getUser();
    this.say(
      'system',
      'Welcome. This screen has two areas: chat on the left, lesson details (TOC and slides) on the right after you open a lesson.'
    );
    const qp = this.route.snapshot.queryParamMap;
    const lessonIdParam = Number(qp.get('lessonId'));
    if (Number.isFinite(lessonIdParam) && lessonIdParam > 0) {
      this.pendingOpenLessonId = lessonIdParam;
      this.pendingGenerateToc = qp.get('generateToc') === '1';
    }
    this.loadLessons({ isInitial: true });
    this.refreshLicenseStatus();
  }

  refreshLicenseStatus(): void {
    if (!this.userId) return;
    this.lessonSvc.getLicenseAvailability(this.userId).subscribe({
      next: (status) => {
        this.licenseStatus = status;
      },
      error: () => {
        this.licenseStatus = { ok: false, message: 'Could not verify license availability.' };
      },
    });
  }

  canCreateLesson(): boolean {
    return this.licenseStatus?.ok !== false;
  }

  licenseBlockMessage(): string {
    return (
      this.licenseStatus?.message ||
      'No licenses available. Contact your administrator to purchase or assign more licenses.'
    );
  }

  say(role: ChatRole, text: string) {
    this.messages.push({ role, text, ts: Date.now() });
  }

  send() {
    const t = (this.inputText || '').trim();
    if (!t) return;
    this.say('user', t);
    this.inputText = '';

    this.handleChatCommand(t);
  }

  private handleChatCommand(text: string) {
    const t = text.trim();
    const tl = t.toLowerCase();

    if (tl === 'help' || tl === '?') {
      this.say(
        'assistant',
        "Commands:\n- All lessons (button) — filter workflow in the list panel\n- Create / AI new lesson …\n- open lesson 5 · open lesson no 5 · #5\n- generate ppt · Generate PPT (header)\n- clear toc — keep lesson, wipe TOC/slides/assets (confirm)\n- delete lesson / delete toc — remove lesson entirely (confirm; blocked if linked to project)\n- open slide SL_… · generate slide 3 · regenerate toc · generate toc · save toc\n  (if linked to project(s): prompted CLEAR or UNLINK before TOC regen)\n- save slide · move slide … · move element …\n- power — lesson properties / TOC editor (power user mode)\n- Full step-by-step guide: toolbar Help or /SME/help"
      );
      return;
    }

    if (tl === 'cancel ai' || tl === 'cancel lesson ai') {
      this.clearAiLessonFlow();
      this.say('assistant', 'AI lesson flow cancelled.');
      return;
    }

    if (tl === 'power') {
      this.goPowerUserMode();
      return;
    }

    if (tl === 'list lessons') {
      const lines = (this.lessons || []).map((l) => `- ${l.lessonId}: ${l.lessonTitle} (v${l.lessonVersion}, ${l.lessonStatus})`);
      this.say('assistant', lines.length ? `Lessons:\n${lines.join('\n')}` : 'No lessons loaded yet.');
      return;
    }

    // e.g. "open lesson 5", "open lesson no 5", "open lesson no. 5", "open lesson number 5", "open lesson #5"
    const openLessonMatch = tl.match(
      /^open\s+lesson\s+(?:(?:no\.?|nr\.?|number|#)\s*)?(\d+)\s*$/
    );
    if (openLessonMatch) {
      const id = Number(openLessonMatch[1]);
      const exists = (this.lessons || []).some((l) => Number(l.lessonId) === id);
      if (!exists) {
        this.say('assistant', `Lesson ${id} not found in list. Type "list lessons".`);
        return;
      }
      this.pickLesson(id);
      return;
    }

    if (tl === 'open toc') {
      if (!this.selectedLessonId) {
        this.say('assistant', 'No lesson selected. Click a lesson or type: open lesson <id>');
        return;
      }
      this.loadTOC();
      return;
    }

    if (tl === 'generate toc') {
      if (!this.selectedLessonId) {
        this.say('assistant', 'No lesson selected. Click a lesson or type: open lesson <id>');
        return;
      }
      this.promptGenerateToc();
      return;
    }

    if (tl === 'save toc') {
      if (!this.toc) {
        this.say('assistant', 'TOC not loaded.');
        return;
      }
      this.saveTOC();
      return;
    }

    if (tl === 'clear toc' || tl === 'clear lesson content') {
      if (!this.selectedLessonId) {
        this.say('assistant', 'No lesson selected. Click a lesson or type: open lesson <id>');
        return;
      }
      this.clearLessonContentFromChat();
      return;
    }

    if (tl === 'delete lesson' || tl === 'delete toc') {
      if (!this.selectedLessonId) {
        this.say('assistant', 'No lesson selected. Click a lesson or type: open lesson <id>');
        return;
      }
      this.deleteLessonFromChat();
      return;
    }

    const regenTocMatch = tl.match(
      /^(?:erase\s+and\s+regenerate\s+toc|regenerate\s+toc|erase\s+toc|erase\s+and\s+regenerate)$/
    );
    if (regenTocMatch) {
      if (!this.selectedLessonId) {
        this.say('assistant', 'No lesson selected. Click a lesson or type: open lesson <id>');
        return;
      }
      this.regenerateTocFromChatImpl();
      return;
    }

    if (tl === 'save slide') {
      if (!this.activeSlideEdit) {
        this.say('assistant', 'No slide open.');
        return;
      }
      this.saveSlideFromChat();
      return;
    }

    if (tl === 'generate ppt') {
      this.generatePptFromChat();
      return;
    }

    const genPptMatch = tl.match(
      /^generate\s+ppt(?:\s+for)?\s+lesson\s+(?:(?:no\.?|nr\.?|number|#)\s*)?(\d+)\s*$/
    );
    const genPptShort = tl.match(/^generate\s+ppt\s+(?:(?:no\.?|nr\.?|number|#)\s*)?(\d+)\s*$/);
    if (genPptMatch || genPptShort) {
      const id = Number((genPptMatch || genPptShort)![1]);
      const exists = (this.lessons || []).some((l) => Number(l.lessonId) === id);
      if (!exists) {
        this.say('assistant', `Lesson ${id} not in list. Refresh lessons or type "list lessons".`);
        return;
      }
      this.generatePptFromChat(id);
      return;
    }

    // move slide element e1 after e2 (before "move slide N after M")
    const moveSlideElMatch = tl.match(/^move\s+(?:slide\s+)?element\s+(\w+)\s+after\s+(\w+)\s*$/);
    if (moveSlideElMatch) {
      const fromId = moveSlideElMatch[1];
      const toId = moveSlideElMatch[2];
      if (!this.activeSlideEdit?.slideElements) {
        this.say('assistant', 'Open a slide first (open slide … / generate slide …).');
        return;
      }
      const arr = this.activeSlideEdit.slideElements;
      const fromIdx = arr.findIndex((e: any) => String(e.elementId).toLowerCase() === fromId.toLowerCase());
      const toIdx = arr.findIndex((e: any) => String(e.elementId).toLowerCase() === toId.toLowerCase());
      if (fromIdx < 0 || toIdx < 0) {
        this.say('assistant', 'Element id not found.');
        return;
      }
      const [item] = arr.splice(fromIdx, 1);
      const insertAt = fromIdx < toIdx ? toIdx : toIdx + 1;
      arr.splice(insertAt, 0, item);
      this.slideDirty = true;
      this.say('assistant', `Moved element ${fromId} after ${toId}.`);
      return;
    }

    const moveSlideMatch = tl.match(
      /^move\s+slide\s+(?!element\b)(?:(?:no\.?|nr\.?|number|#)\s*)?(\S+)\s+after\s+(?:(?:no\.?|nr\.?|number|#)\s*)?(\S+)\s*$/
    );
    if (moveSlideMatch) {
      if (!this.toc?.sections?.length) {
        this.say('assistant', 'Load a lesson TOC first (open lesson …).');
        return;
      }
      const fromRef = moveSlideMatch[1];
      const toRef = moveSlideMatch[2];
      if (this.moveSlideAfterInToc(fromRef, toRef)) {
        this.say('assistant', `Moved slide (${fromRef}) after (${toRef}). Save TOC when ready.`);
      }
      return;
    }

    const openOrGenSlideMatch = text.match(/^(?:open|generate)\s+slide\s+(.+)$/i);
    if (openOrGenSlideMatch) {
      const refRaw = openOrGenSlideMatch[1].trim();
      const ref = this.normalizeSlideRef(refRaw);
      if (!this.toc?.sections?.length) {
        this.say('assistant', 'Load TOC first (open a lesson).');
        return;
      }
      const resolved = this.resolveTocSlideRef(ref);
      if (!resolved) {
        this.say(
          'assistant',
          `Slide not found for “${refRaw}”. Use a number (1 = first in TOC) or full slide id (e.g. SL_…).`
        );
        return;
      }
      this.openSlide(resolved.section, resolved.slide);
      return;
    }

    const insertAfterMatch = tl.match(/^insert\s+element\s+after\s+(\w+)\s*$/);
    if (insertAfterMatch) {
      const afterId = insertAfterMatch[1];
      if (!this.activeSlideEdit?.slideElements) {
        this.say('assistant', 'Open a slide first.');
        return;
      }
      const idx = this.activeSlideEdit.slideElements.findIndex(
        (e: any) => String(e.elementId).toLowerCase() === afterId.toLowerCase()
      );
      if (idx < 0) {
        this.say('assistant', 'Element id not found.');
        return;
      }
      this.insertSlideElementAfter(idx);
      this.slideDirty = true;
      this.say('assistant', `Inserted new element after ${afterId}.`);
      return;
    }

    const addBulletMatch = t.match(/^add\s+bullet\s+after\s+(\w+)\s+(.+)$/i);
    if (addBulletMatch) {
      const afterId = addBulletMatch[1];
      const bulletText = addBulletMatch[2].trim();
      if (!this.activeSlideEdit?.slideElements) {
        this.say('assistant', 'Open a slide first.');
        return;
      }
      const arr = this.activeSlideEdit.slideElements;
      const idx = arr.findIndex((e: any) => String(e.elementId).toLowerCase() === afterId.toLowerCase());
      if (idx < 0) {
        this.say('assistant', 'Element id not found.');
        return;
      }
      const base = arr[idx];
      arr.splice(idx + 1, 0, {
        elementId: this.uuid(),
        elementType: 'TEXT',
        bulletFlag: 'Y',
        indentLevel: base?.indentLevel ?? 0,
        imageCaption: '',
        content: bulletText,
        notes: '',
        imagePrompt: null,
      });
      this.slideDirty = true;
      this.say('assistant', `Added bullet after ${afterId}.`);
      return;
    }

    if (this.shouldRouteToAiLessonChat(t)) {
      this.runAiLessonInterpret(t);
      return;
    }

    this.say(
      'assistant',
      'Command not recognized. Type help, use **All lessons**, or start with “Create a new lesson …” for AI assist.'
    );
  }

  private shouldRouteToAiLessonChat(t: string): boolean {
    if (this.aiLessonChatActive) return true;
    return (
      /^\s*create\s+(a\s+)?new\s+lesson\b/i.test(t) ||
      /^\s*create\s+lesson\b/i.test(t)
    );
  }

  startAiLessonChat() {
    if (!this.maybeSaveBeforeNavigate('lesson')) return;
    this.mode = 'pickLesson';
    this.clearAiLessonFlow();
    this.aiLessonChatActive = true;
    this.aiLessonPartial = null;
    this.showLessonPicker = false;
    this.say(
      'assistant',
      'AI lesson mode: describe the lesson in one message (title, course, audience, slide count, examples, etc.). I will fill the fields and ask only for what is still required, then create the lesson and open the TOC on the right.\nType cancel ai to stop.'
    );
  }

  toggleLessonPicker() {
    if (!this.showLessonPicker) {
      if (!this.maybeSaveBeforeNavigate('lesson')) return;
    }
    this.showLessonPicker = !this.showLessonPicker;
  }

  refreshLessonsFromChat() {
    if (!this.maybeSaveBeforeNavigate('lesson')) return;
    this.loadLessons({ announceRefresh: true });
  }

  onCancelCreateForm() {
    this.mode = 'pickLesson';
    this.say('assistant', 'Create form closed.');
  }

  private lessonLinesForChat(): string {
    return (this.lessons || [])
      .map((l) => {
        const life = (l.lessonLifecycleStatus || 'IN_PROGRESS') as string;
        return `• #${l.lessonId} — ${l.lessonTitle} (${l.lessonStatus}, ${life})`;
      })
      .join('\n');
  }

  private buildWelcomeAssistantText(): string {
    const q =
      'Would you like to create a **new lesson** or work on an **earlier lesson**?\n\n';
    const hints =
      'Use **All lessons** to open the list (you’ll be asked to save if TOC or slide edits are pending). In chat you can say e.g. **open lesson 5** or **open lesson no 5**, “Create a new lesson …”, or **help**. Step-by-step guide: toolbar **Help**.\n\n';
    if (!this.lessons?.length) {
      return q + 'You don’t have any lessons yet. Try **AI new lesson** or **Create (form)**.\n\n' + hints;
    }
    return q + 'Your lessons:\n' + this.lessonLinesForChat() + '\n\n' + hints;
  }

  private clearAiLessonFlow() {
    this.aiLessonChatActive = false;
    this.aiLessonPartial = null;
    this.lessonAiBusy = false;
  }

  private runAiLessonInterpret(userMessage: string) {
    this.lessonAiBusy = true;
    this.aiLessonChatActive = true;
    this.lessonSvc
      .interpretSmeLessonChat(this.userId, userMessage, this.aiLessonPartial)
      .pipe(finalize(() => (this.lessonAiBusy = false)))
      .subscribe({
        next: (data) => {
          if (!data) {
            this.say('assistant', 'AI assist: empty response.');
            return;
          }
          this.aiLessonPartial = data.lessonData;
          this.say('assistant', data.assistantMessage);
          if (data.readyToCreate && data.lessonData) {
            this.say('assistant', 'Mandatory fields are complete. Creating lesson and loading TOC…');
            this.createLessonQuick(this.normalizeLessonCreatePayload(data.lessonData), { fromAi: true });
          }
        },
        error: (err) => {
          const msg = err?.error?.error || err?.error?.message || err?.message || 'Request failed';
          this.say('assistant', 'AI assist failed: ' + msg + ' You can edit your message and try again; no need to restart the app.');
        },
      });
  }

  // ---------- Lesson ----------
  loadLessons(opts?: { isInitial?: boolean; announceRefresh?: boolean }) {
    this.loadingLessons = true;
    const filt = this.lessonLifecycleFilter?.trim() || null;
    this.lessonSvc.getLessons(this.userId, filt).subscribe({
      next: (rows) => {
        this.lessons = rows || [];
        this.loadingLessons = false;
        if (this.selectedLessonId) {
          const row = this.lessons.find((l) => l.lessonId === this.selectedLessonId);
          if (row) {
            this.selectedLessonLifecycleStatus = (row.lessonLifecycleStatus || 'IN_PROGRESS') as any;
          }
        }
        if (opts?.isInitial) {
          this.say('assistant', this.buildWelcomeAssistantText());
          this.applyPendingLessonOpen();
        } else if (opts?.announceRefresh) {
          this.say(
            'assistant',
            'Lesson list refreshed.\n' + (this.lessons.length ? this.lessonLinesForChat() : 'No lessons yet.')
          );
        }
      },
      error: () => {
        this.loadingLessons = false;
        this.say('assistant', 'Failed to load lessons.');
      },
    });
  }

  pickLesson(lessonId: number) {
    if (!this.maybeSaveBeforeNavigate('lesson')) return;
    this.showLessonPicker = false;
    this.clearAiLessonFlow();
    this.tocSlideBeingEdited = null;
    this.selectedLessonId = lessonId;
    const row = this.lessons?.find((l) => l.lessonId === lessonId);
    this.selectedLessonLifecycleStatus = ((row?.lessonLifecycleStatus || 'IN_PROGRESS') as any) ?? 'IN_PROGRESS';
    this.say('assistant', `Selected lesson ${lessonId}. Loading…`);
    this.loadReferences();
    this.loadLessonQuizDefaults(lessonId, () => {
      const status = String(row?.lessonStatus || '').toUpperCase();
      if (status === 'EMPTY') {
        if (this.lessonNeedsReferenceBeforeToc()) {
          this.mode = 'pickLesson';
          this.say(
            'assistant',
            `This is a document-based lesson. ${this.referenceRequiredBeforeTocMessage('open')}`,
          );
          return;
        }
        this.beginAutoTocForEmptyLesson('No TOC yet — generating table of contents from lesson properties…');
        return;
      }
      this.loadTOC();
    });
  }

  selectedLessonStatus(): string {
    if (!this.selectedLessonId) return '';
    const row = this.lessons?.find((l) => Number(l.lessonId) === Number(this.selectedLessonId));
    return String(row?.lessonStatus || '').toUpperCase();
  }

  referencesLocked(): boolean {
    const st = this.selectedLessonStatus();
    return !!st && st !== 'EMPTY' && st !== 'TOC';
  }

  /** document_strict and document_reference must upload refs before TOC AI runs. */
  lessonNeedsReferenceBeforeToc(): boolean {
    const mode = String(this.selectedLessonContentMode || '').toLowerCase();
    return mode === 'document_strict' || mode === 'document_reference';
  }

  isDocumentStrictSelected(): boolean {
    return String(this.selectedLessonContentMode || '').toLowerCase() === 'document_strict';
  }

  hasPrimaryReference(): boolean {
    return (this.references || []).some(
      (r) => String(r?.referenceRole || '').toLowerCase() === 'primary',
    );
  }

  /** Any uploaded reference (for document_reference TOC gate). */
  hasAnyReference(): boolean {
    return (this.references || []).length > 0;
  }

  /** True when TOC may run for the current document mode (refs present as required). */
  referencesReadyForToc(): boolean {
    if (this.hasExpiredOpenAiReferences()) return false;
    if (!this.lessonNeedsReferenceBeforeToc()) return true;
    if (this.isDocumentStrictSelected()) return this.hasPrimaryReference();
    return this.hasAnyReference();
  }

  isReferenceOpenAiExpired(ref: {
    openAiFileExpiredFlag?: boolean | number | string;
    openAiFileExpired?: boolean | number | string;
  } | null | undefined): boolean {
    if (!ref) return false;
    const flag = ref.openAiFileExpiredFlag ?? ref.openAiFileExpired;
    return flag === true || flag === 1 || flag === '1' || String(flag).toLowerCase() === 'true';
  }

  hasExpiredOpenAiReferences(): boolean {
    return (this.references || []).some((r) => this.isReferenceOpenAiExpired(r));
  }

  expiredOpenAiReferenceNames(): string {
    return (this.references || [])
      .filter((r) => this.isReferenceOpenAiExpired(r))
      .map((r) => String(r?.label || r?.fileName || r?.refId || 'file').trim())
      .filter(Boolean)
      .join(', ');
  }

  private expiredOpenAiBlockMessage(): string {
    const names = this.expiredOpenAiReferenceNames();
    const list = names ? ` (${names})` : '';
    return `OpenAI reference upload expired${list}. Click "Refresh OpenAI upload" under Reference files before generating TOC or slides.`;
  }

  refreshExpiredOpenAiUploads(): void {
    if (!this.selectedLessonId || this.refRefreshOpenAiBusy) return;
    this.refRefreshOpenAiBusy = true;
    this.refUploadError = '';
    this.refUploadStatus = 'Refreshing OpenAI uploads…';
    this.lessonSvc.refreshLessonReferenceOpenAi(this.userId, this.selectedLessonId).subscribe({
      next: (res: unknown) => {
        this.refRefreshOpenAiBusy = false;
        const data = (unwrapVioResponseData(res) ?? res) as {
          references?: unknown[];
          refreshed?: number;
        };
        if (Array.isArray(data?.references)) {
          this.references = data.references;
        } else {
          this.loadReferences();
        }
        const n = Number(data?.refreshed);
        this.refUploadStatus =
          Number.isFinite(n) && n > 0
            ? `Refreshed ${n} OpenAI file upload(s). You can generate TOC / slides now.`
            : 'OpenAI uploads are up to date.';
        this.say('assistant', this.refUploadStatus);
      },
      error: (err: unknown) => {
        this.refRefreshOpenAiBusy = false;
        this.refUploadStatus = '';
        this.refUploadError = extractApiErrorMessage(err, 'Could not refresh OpenAI uploads.');
        this.say('assistant', this.refUploadError);
      },
    });
  }

  private referenceRequiredBeforeTocMessage(context: 'create' | 'open' | 'clear' | 'generate'): string {
    if (this.hasExpiredOpenAiReferences()) {
      return this.expiredOpenAiBlockMessage();
    }
    if (this.isDocumentStrictSelected()) {
      if (context === 'generate') {
        return 'Document-specific lessons need a primary reference PDF uploaded before generating the TOC.';
      }
      if (context === 'create') {
        return 'Upload a primary reference PDF in Reference files below (Primary = Yes), then click Generate TOC.';
      }
      return 'Upload reference file(s) in Reference files below — set Primary Reference to Yes — then click Generate TOC.';
    }
    if (context === 'generate') {
      return 'Lessons that refer to a document need at least one reference file uploaded before generating the TOC.';
    }
    if (context === 'create') {
      return 'Upload reference file(s) in Reference files below, then click Generate TOC.';
    }
    return 'Upload reference file(s) in Reference files below, then click Generate TOC.';
  }

  loadReferences(): void {
    if (!this.selectedLessonId) {
      this.references = [];
      return;
    }
    this.lessonSvc.listLessonReferences(this.userId, this.selectedLessonId).subscribe({
      next: (rows) => {
        this.references = Array.isArray(rows) ? rows : [];
      },
      error: (err: unknown) => {
        this.references = [];
        this.refUploadError = extractApiErrorMessage(err, 'Could not load reference file list.');
      },
    });
  }

  onReferenceFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = '';
    if (!files.length || !this.selectedLessonId) return;
    void this.uploadReferenceFiles(files);
  }

  primaryReferenceYn(ref: { referenceRole?: string } | null | undefined): 'Y' | 'N' {
    return String(ref?.referenceRole || 'supplemental').toLowerCase() === 'primary' ? 'Y' : 'N';
  }

  setPrimaryReference(ref: any, value: 'Y' | 'N'): void {
    const role = value === 'Y' ? 'primary' : 'supplemental';
    if (!this.selectedLessonId || ref.referenceRole === role) return;
    this.lessonSvc
      .updateLessonReference(this.userId, this.selectedLessonId, ref.refId, { referenceRole: role })
      .subscribe({
        next: () => {
          ref.referenceRole = role;
        },
        error: () => alert('Could not update reference role'),
      });
  }

  toggleReferenceFlag(ref: any, field: 'useForToc' | 'useForSlides', event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const value: 'Y' | 'N' = checked ? 'Y' : 'N';
    if (!this.selectedLessonId) return;
    this.lessonSvc
      .updateLessonReference(this.userId, this.selectedLessonId, ref.refId, { [field]: value })
      .subscribe({
        next: () => {
          ref[field] = value;
        },
        error: () => {
          (event.target as HTMLInputElement).checked = !checked;
          alert('Could not update reference');
        },
      });
  }

  deleteReference(refId: string): void {
    if (!this.selectedLessonId || !confirm('Remove this reference file?')) return;
    this.lessonSvc.deleteLessonReference(this.userId, this.selectedLessonId, refId).subscribe({
      next: () => this.loadReferences(),
      error: () => alert('Could not delete reference'),
    });
  }

  formatRefSize(bytes: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
      reader.readAsDataURL(file);
    });
  }

  private async uploadReferenceFiles(files: File[]): Promise<void> {
    if (!this.selectedLessonId) return;
    this.refUploadError = '';
    const errors: string[] = [];
    const valid: File[] = [];

    for (const file of files) {
      if (!this.refAllowedMimeTypes.includes(file.type)) {
        errors.push(`${file.name}: allowed types are PDF, PNG, JPEG, GIF, WebP`);
        continue;
      }
      if (file.size > this.refMaxBytes) {
        errors.push(`${file.name}: exceeds ${this.refMaxMbLabel} limit`);
        continue;
      }
      valid.push(file);
    }

    if (!valid.length) {
      this.refUploadError = errors.join('\n');
      return;
    }

    this.refUploading = true;
    let uploaded = 0;
    for (const file of valid) {
      this.refUploadStatus = `Uploading ${uploaded + 1} of ${valid.length}: ${file.name}`;
      try {
        const dataUrl = await this.readFileAsDataUrl(file);
        await new Promise<void>((resolve, reject) => {
          this.lessonSvc
            .uploadLessonReference(this.userId, this.selectedLessonId!, file.name, dataUrl, {
              label: file.name,
              mimeType: file.type,
              referenceRole: this.refUploadPrimary === 'Y' ? 'primary' : 'supplemental',
            })
            .subscribe({
              next: () => resolve(),
              error: (err: unknown) => {
                reject(new Error(extractApiErrorMessage(err, `Upload failed: ${file.name}`)));
              },
            });
        });
        uploaded += 1;
      } catch (err: unknown) {
        errors.push(err instanceof Error ? err.message : String(err));
      }
    }

    this.refUploading = false;
    this.refUploadStatus = '';
    if (uploaded > 0 && !errors.length) {
      this.refUploadStatus = `Uploaded ${uploaded} file(s).`;
      this.say('assistant', `Uploaded ${uploaded} reference file(s).`);
    } else if (uploaded > 0) {
      this.refUploadStatus = `Uploaded ${uploaded} of ${valid.length} file(s).`;
    }
    this.loadReferences();
    if (errors.length) {
      this.refUploadError = errors.join('\n');
    }
  }

  selectedLessonIsEmpty(): boolean {
    if (!this.selectedLessonId) return false;
    const row = this.lessons?.find((l) => Number(l.lessonId) === Number(this.selectedLessonId));
    return String(row?.lessonStatus || '').toUpperCase() === 'EMPTY';
  }

  private applyPendingLessonOpen(): void {
    if (!this.pendingOpenLessonId) return;
    const lessonId = this.pendingOpenLessonId;
    const generateToc = this.pendingGenerateToc;
    this.pendingOpenLessonId = null;
    this.pendingGenerateToc = false;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { lessonId: null, generateToc: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    const exists = (this.lessons || []).some((l) => Number(l.lessonId) === lessonId);
    if (!exists) {
      this.say('assistant', `Lesson ${lessonId} not found in list.`);
      return;
    }
    if (generateToc) {
      this.showLessonPicker = false;
      this.clearAiLessonFlow();
      this.tocSlideBeingEdited = null;
      this.selectedLessonId = lessonId;
      const row = this.lessons.find((l) => Number(l.lessonId) === lessonId);
      this.selectedLessonLifecycleStatus = ((row?.lessonLifecycleStatus || 'IN_PROGRESS') as any) ?? 'IN_PROGRESS';
      this.loadReferences();
      this.loadLessonQuizDefaults(lessonId, () => {
        if (this.lessonNeedsReferenceBeforeToc()) {
          this.mode = 'pickLesson';
          this.say(
            'assistant',
            `Lesson content was cleared. ${this.referenceRequiredBeforeTocMessage('clear')}`,
          );
          return;
        }
        this.beginAutoTocForEmptyLesson('Lesson content was cleared — generating a new table of contents…');
      });
      return;
    }
    this.pickLesson(lessonId);
  }

  /** After clear or when opening an EMPTY lesson — auto-generate TOC when quiz settings allow. */
  private beginAutoTocForEmptyLesson(introMessage?: string): void {
    if (!this.selectedLessonId) return;
    if (this.hasExpiredOpenAiReferences()) {
      this.mode = 'pickLesson';
      this.say('assistant', this.expiredOpenAiBlockMessage());
      return;
    }
    if (!this.referencesReadyForToc()) {
      this.mode = 'pickLesson';
      this.say('assistant', this.referenceRequiredBeforeTocMessage('generate'));
      return;
    }
    if (introMessage) {
      this.say('assistant', introMessage);
    }
    this.loadLessonQuizDefaults(this.selectedLessonId, () => {
      const quizConfig = this.quizConfigForTocGeneration(true);
      if (quizConfig === null) {
        this.mode = 'pickLesson';
        this.say(
          'assistant',
          'TOC generation cancelled. Set quiz counts in lesson properties (power user mode) or click Generate TOC when ready.',
        );
        return;
      }
      this.loadTOC({ autoGenerate: true, quizConfig });
    });
  }

  private loadLessonQuizDefaults(lessonId: number, onReady?: () => void): void {
    this.lessonSvc.getLessonMeta(this.userId, lessonId).subscribe({
      next: (res: any) => {
        const lesson = unwrapLessonRow(res);
        this.selectedLessonContentMode = String(lesson?.['lessonContentMode'] || 'generic').toLowerCase();
        const prefs = lesson?.['preferences'];
        this.savedQuizDefaults = quizConfigFromLessonPreferences(
          prefs && typeof prefs === 'object' ? (prefs as Record<string, unknown>) : undefined,
        );
        onReady?.();
      },
      error: () => {
        this.selectedLessonContentMode = 'generic';
        this.savedQuizDefaults = {};
        onReady?.();
      },
    });
  }

  /** Quiz settings for TOC AI — use lesson properties when set; prompt only if needed. */
  private quizConfigForTocGeneration(interactive: boolean): QuizConfig | null {
    const partial = quizConfigFromLessonPreferences(
      this.savedQuizDefaults as Record<string, unknown> | undefined,
    );
    // Assessment off → no quiz AI (do not overwrite a Y preference unless counts resolve to off).
    if (partial.lessonAssessment !== 'Y') {
      return {
        lessonAssessment: 'N',
        enabled: false,
        questionsPerSection: 0,
        endOfLessonCount: 0,
        simpleCount: 0,
        complexCount: 0,
        appliedFinalCount: 0,
      };
    }
    const hasCounts = (partial.questionsPerSection ?? 0) > 0 || (partial.endOfLessonCount ?? 0) > 0;
    if (hasCounts) {
      return {
        lessonAssessment: 'Y',
        enabled: true,
        questionsPerSection: partial.questionsPerSection ?? 0,
        endOfLessonCount: partial.endOfLessonCount ?? 0,
        simpleCount: partial.simpleCount ?? 0,
        complexCount: partial.complexCount ?? 0,
        appliedFinalCount: partial.appliedFinalCount ?? 0,
      };
    }
    // Assessment Y but counts not set yet — prompt (or cancel) instead of forcing N.
    if (!interactive) return null;
    return promptQuizConfig(this.savedQuizDefaults);
  }

  goCreateLesson() {
    if (!this.maybeSaveBeforeNavigate('lesson')) return;
    this.clearAiLessonFlow();
    this.showLessonPicker = false;
    this.selectedLessonId = null;
    this.selectedLessonLifecycleStatus = 'IN_PROGRESS';
    this.toc = null;
    this.activeSlide = null;
    this.activeSlideEdit = null;
    this.activeSlideId = null;
    this.activeSlideTitle = null;
    this.tocSlideBeingEdited = null;
    this.imageLoadState = {};
    this.slideDirty = false;
    this.tocDirty = false;
    this.showSlideNotes = false;
    this.showElementNotes = false;
    this.previewExpanded = false;
    this.showAdvancedElementFields = false;
    this.mode = 'createLesson';
    this.refreshLicenseStatus();
    if (!this.canCreateLesson()) {
      this.say('assistant', this.licenseBlockMessage());
      return;
    }
    this.say(
      'assistant',
      'Create lesson: fill required fields below (title, type, approx slide count). Optional fields can be left blank.'
    );
  }

  // We reuse the existing createLesson API which expects the same payload as the form.
  createLessonQuick(formValue: any, opts?: { fromAi?: boolean }) {
    if (!this.canCreateLesson()) {
      this.say('assistant', this.licenseBlockMessage());
      return;
    }
    const payload = this.normalizeLessonCreatePayload(formValue);
    this.lessonSvc.createLesson(this.userId, payload).subscribe({
      next: (lessons) => {
        if (opts?.fromAi) {
          this.clearAiLessonFlow();
        }
        this.say('assistant', 'Lesson created. Loading lessons list…');
        this.mode = 'pickLesson';
        this.loadLessons();
        this.refreshLicenseStatus();
        // try to select newest by max lessonId if present
        const list = Array.isArray(lessons) ? lessons : lessons ? [lessons] : [];
        if (list.length) {
          const max = list.reduce((m: any, r: any) => (r.lessonId > m.lessonId ? r : m), list[0]);
          const newLessonId = Number(max.lessonId);
          this.selectedLessonId = newLessonId;
          this.selectedLessonContentMode = String(payload.lessonContentMode || 'generic').toLowerCase();
          // Keep quiz prefs from create payload so auto-TOC does not wipe lessonAssessment to N.
          this.savedQuizDefaults = quizConfigFromLessonPreferences(
            payload.preferences as Record<string, unknown>,
          );
          this.loadReferences();
          if (this.lessonNeedsReferenceBeforeToc()) {
            this.mode = 'pickLesson';
            this.say(
              'assistant',
              `Lesson ${newLessonId} created. ${this.referenceRequiredBeforeTocMessage('create')}`,
            );
            return;
          }
          this.say('assistant', `Selected lesson ${newLessonId}. Loading TOC…`);
          this.loadLessonQuizDefaults(newLessonId, () => this.loadTOC());
        } else {
          this.say('assistant', 'Lesson may have been created but the server returned no lesson list; try Refresh lessons.');
        }
      },
      error: (err) => {
        const msg =
          err?.error?.error ||
          err?.error?.errorDescription ||
          err?.error?.message ||
          err?.message ||
          'Failed to create lesson.';
        this.say('assistant', 'Create lesson failed: ' + msg + ' You can fix the fields and try again.');
        this.refreshLicenseStatus();
        if (opts?.fromAi) {
          this.clearAiLessonFlow();
        }
      },
    });
  }

  /** Ensure nested preferences + types match createLesson / form expectations (AI payloads can omit branches). */
  private normalizeLessonCreatePayload(src: any): any {
    if (!src || typeof src !== 'object') {
      return src;
    }
    const prefs = src.preferences && typeof src.preferences === 'object' ? src.preferences : {};
    const tech = src.techpreferences && typeof src.techpreferences === 'object' ? src.techpreferences : {};
    let style = src.lessonStyle;
    if (Array.isArray(style)) {
      style = style[0];
    }
    if (style != null && typeof style !== 'string') {
      style = String(style);
    }
    if (!style) style = 'simple';
    const slideCountRaw = String(src.lessonSlideCount ?? '').trim();
    return {
      lessonTitle: String(src.lessonTitle ?? '').trim(),
      lessonInstructions: String(src.lessonInstructions ?? ''),
      lessonSubject: String(src.lessonSubject ?? ''),
      lessonType: src.lessonType,
      lessonContentMode: String(src.lessonContentMode ?? 'generic').toLowerCase(),
      lessonVersion: src.lessonVersion ?? '1.0',
      lessonStatus: src.lessonStatus ?? 'EMPTY',
      lessonSlideCount: slideCountRaw || '10',
      lessonLearnerProfile: String(src.lessonLearnerProfile ?? ''),
      lessonStyle: style,
      lessonDeliveredInPerson: String(src.lessonDeliveredInPerson ?? 'N').toUpperCase() === 'Y' ? 'Y' : 'N',
      preferences: {
        lessonCaseStudy: prefs.lessonCaseStudy ?? 'NA',
        lessonExamples: prefs.lessonExamples ?? 'Y',
        lessonAssessment: prefs.lessonAssessment ?? 'NA',
        lessonSectionTOC: prefs.lessonSectionTOC ?? 'Y',
        lessonQuizPerSection: Number(prefs.lessonQuizPerSection) || 0,
        lessonQuizEndCount: Number(prefs.lessonQuizEndCount) || 0,
        lessonQuizSimpleCount: Number(prefs.lessonQuizSimpleCount) || 0,
        lessonQuizComplexCount: Number(prefs.lessonQuizComplexCount) || 0,
        lessonQuizAppliedFinalCount: Number(prefs.lessonQuizAppliedFinalCount) || 0,
      },
      techpreferences: {
        lessonDerivations: tech.lessonDerivations ?? 'NA',
        lessonGoodPractices: tech.lessonGoodPractices ?? 'NA',
        lessonSafetyInstr: tech.lessonSafetyInstr ?? 'NA',
        lessonIncludeStandards: tech.lessonIncludeStandards ?? 'NA',
      },
    };
  }

  // ---------- TOC ----------
  loadTOC(opts?: { autoGenerate?: boolean; quizConfig?: QuizConfig | null }) {
    if (!this.selectedLessonId) return;
    if (this.tocBusy) {
      this.say('assistant', 'TOC generation is already in progress — please wait.');
      return;
    }
    this.tocBusy = true;
    this.tocProgressMessage = '';
    let finished = false;
    const finishBusy = () => {
      if (!finished) {
        finished = true;
        this.tocBusy = false;
        this.tocProgressMessage = '';
      }
    };

    void this.tocSvc
      .streamGetTOC(
        this.userId,
        this.selectedLessonId,
        (ev) => {
          if (ev.type === 'progress' && ev.message) {
            this.tocProgressMessage = String(ev.message);
          }
          if (ev.type === 'complete' && ev['data']) {
            this.toc = this.normalizeTocFromApi(ev['data']);
            this.mode = 'toc';
            this.tocDirty = false;
            this.say('assistant', 'TOC loaded. You can reorder/add/delete sections and slides, then save.');
            finishBusy();
          }
          if (ev.type === 'error') {
            const msg = String(ev.message || 'Failed to load TOC.');
            if (this.isTocMissingMessage(msg)) {
              this.beginTocGenerationAfterMissing();
            } else {
              this.handleAiFailure('TOC failed', msg, () => {
                this.say('assistant', 'Continuing — you can retry TOC generation when ready.');
              });
              finishBusy();
            }
          }
        },
        {
          autoGenerate: opts?.autoGenerate,
          quizConfig: opts?.quizConfig,
        },
      )
      .catch((err: Error) => {
        const msg = err?.message || 'Failed to load TOC.';
        if (this.isTocMissingMessage(msg)) {
          this.beginTocGenerationAfterMissing();
        } else {
          this.handleAiFailure('TOC failed', msg, () => {
            this.say('assistant', 'Continuing — you can retry TOC generation when ready.');
          });
          finishBusy();
        }
      });
  }

  private beginTocGenerationAfterMissing() {
    const quizConfig = this.quizConfigForTocGeneration(true);
    if (quizConfig === null) {
      this.say('assistant', 'TOC generation cancelled.');
      this.tocBusy = false;
      return;
    }
    this.say('assistant', 'Generating table of contents with AI…');
    this.runGenerateTocWithQuizConfig(quizConfig);
  }

  private isTocMissingMessage(msg: string): boolean {
    const m = msg.toLowerCase();
    return m.includes('generate toc') || m.includes('no table of contents');
  }

  private handleAiFailure(title: string, detail: string, onContinue?: () => void): void {
    this.say('assistant', `${title}: ${detail}`);
    if (confirmContinueAfterAiError(title, detail)) {
      onContinue?.();
    }
  }

  private promptGenerateToc(): void {
    if (this.hasExpiredOpenAiReferences()) {
      this.say('assistant', this.expiredOpenAiBlockMessage());
      return;
    }
    const quizConfig = this.quizConfigForTocGeneration(true);
    if (quizConfig === null) {
      this.say('assistant', 'TOC generation cancelled.');
      return;
    }
    if (this.tocBusy) {
      this.say('assistant', 'TOC generation is already in progress — please wait.');
      return;
    }
    let linkedProjectAction: 'clear' | 'unlink' | undefined;
    if (this.isLessonLinkedToProject() && this.toc) {
      const action = this.promptClearOrUnlinkLinkedProjects();
      if (!action) {
        this.say('assistant', 'TOC generation cancelled.');
        return;
      }
      linkedProjectAction = action;
    }
    this.savedQuizDefaults = quizConfig;
    this.tocBusy = true;
    this.say('assistant', 'Generating table of contents with AI…');
    this.runGenerateTocWithQuizConfig(quizConfig, linkedProjectAction);
  }

  private runGenerateTocWithQuizConfig(
    quizConfig: QuizConfig,
    linkedProjectAction?: 'clear' | 'unlink' | null,
  ): void {
    this.tocSvc.generateTOC(this.userId, this.selectedLessonId!, quizConfig, linkedProjectAction).subscribe({
      next: (res: any) => {
        const data = res?.Data ?? res?.data ?? res;
        if (data) {
          this.toc = this.normalizeTocFromApi(data);
          this.mode = 'toc';
          this.tocDirty = false;
          this.say('assistant', 'TOC generated. You can reorder/add/delete sections and slides, then save.');
        } else {
          this.loadTOC();
        }
        this.tocBusy = false;
        this.loadLessons();
      },
      error: (err) => {
        this.tocBusy = false;
        const msg = extractApiErrorMessage(err, 'TOC generation failed.');
        this.handleAiFailure('TOC generation failed', msg, () => {
          this.say('assistant', 'Continuing — use lesson properties or retry when ready.');
        });
      },
    });
  }

  saveTOC() {
    if (!this.selectedLessonId || !this.toc) return;
    this.tocSvc.saveTOC(this.userId, this.selectedLessonId, this.toc).subscribe({
      next: (res: unknown) => {
        this.tocDirty = false;
        this.say('assistant', 'TOC saved.');
        const warnings = (res as { warnings?: string[] })?.warnings;
        for (const w of warnings || []) {
          this.say('assistant', 'Note: ' + w);
        }
      },
      error: () => this.say('assistant', 'Failed to save TOC.'),
    });
  }

  private isLessonLinkedToProject(lessonId?: number | null): boolean {
    const id = lessonId ?? this.selectedLessonId;
    const row = this.lessons?.find((l) => Number(l.lessonId) === Number(id));
    return !!(row && (Number(row.linkedToProject) === 1 || row.linkedToProject === true));
  }

  /** Project short codes linked to the selected lesson (may be multiple). */
  private linkedProjectCodesForSelected(lessonId?: number | null): string[] {
    const id = lessonId ?? this.selectedLessonId;
    const row = this.lessons?.find((l) => Number(l.lessonId) === Number(id)) as
      | { linkedProjectCodes?: string | string[]; linkedToProject?: unknown }
      | undefined;
    if (!row) return [];
    const raw = row.linkedProjectCodes;
    if (Array.isArray(raw)) {
      return raw.map((c) => String(c || '').trim()).filter(Boolean);
    }
    return String(raw || '')
      .split(/[,;]/)
      .map((c) => c.trim())
      .filter(Boolean);
  }

  /**
   * When regenerating TOC for a lesson linked to one or more projects, ask CLEAR vs UNLINK.
   * Returns null if the user cancels. Call only when the lesson is linked.
   */
  private promptClearOrUnlinkLinkedProjects(): 'clear' | 'unlink' | null {
    const codes = this.linkedProjectCodesForSelected();
    const list = codes.length ? codes.join(', ') : '(linked project)';
    const answer = window.prompt(
      `This lesson is linked to project(s): ${list}\n\n` +
        `Type CLEAR — clear slides/lines in ALL linked projects (keep links), then regenerate TOC.\n` +
        `Type UNLINK — unlink this lesson from those projects (keep their existing slides), then regenerate TOC.\n` +
        `Leave blank or Cancel to abort.`,
      'CLEAR',
    );
    if (answer == null) return null;
    const a = String(answer).trim().toUpperCase();
    if (a === 'CLEAR') return 'clear';
    if (a === 'UNLINK') return 'unlink';
    this.say('assistant', 'Enter CLEAR or UNLINK (or cancel). TOC regeneration aborted.');
    return null;
  }

  selectedLessonLinkedToProject(): boolean {
    return this.isLessonLinkedToProject();
  }

  canClearSelectedLesson(): boolean {
    if (!this.selectedLessonId || this.selectedLessonLinkedToProject()) return false;
    const row = this.lessons?.find((l) => Number(l.lessonId) === Number(this.selectedLessonId));
    const st = String(row?.lessonStatus || 'EMPTY').toUpperCase();
    return st !== 'EMPTY' || !!this.toc;
  }

  clearLessonContentFromChat(): void {
    this.clearLessonContentConfirmed();
  }

  private normalizeTocFromApi(raw: unknown): TocTree {
    const d = (raw || {}) as TocTree;
    return {
      ...d,
      openingNote: parseTocNoteFromApi(d.openingNote),
      closingNote: parseTocNoteFromApi(d.closingNote),
      sections: Array.isArray(d.sections) ? d.sections : [],
    };
  }

  regenerateTocFromChat(): void {
    this.regenerateTocFromChatImpl();
  }

  generateTocFromChat(): void {
    if (!this.selectedLessonId) {
      this.say('assistant', 'No lesson selected.');
      return;
    }
    if (this.hasExpiredOpenAiReferences()) {
      this.say('assistant', this.expiredOpenAiBlockMessage());
      return;
    }
    if (!this.referencesReadyForToc()) {
      this.say('assistant', this.referenceRequiredBeforeTocMessage('generate'));
      return;
    }
    this.promptGenerateToc();
  }

  private clearLessonContentConfirmed(): void {
    if (this.isLessonLinkedToProject()) {
      const codes = this.linkedProjectCodesForSelected();
      const list = codes.length ? codes.join(', ') : 'a creator project';
      this.say('assistant', `This lesson is linked to project(s): ${list}. Unlink before clearing content.`);
      return;
    }
    if (
      !window.confirm(
        'Clear lesson content? This keeps the lesson record but removes TOC, lesson slides, project slides, generated images/audio, references, and PPT files. You can generate a fresh TOC afterward.',
      )
    ) {
      this.say('assistant', 'Clear cancelled.');
      return;
    }
    this.tocBusy = true;
    this.tocSvc.clearLessonContent(this.userId, this.selectedLessonId!).subscribe({
      next: () => {
        this.toc = null;
        this.tocDirty = false;
        this.activeSlide = null;
        this.activeSlideEdit = null;
        this.activeSlideId = null;
        this.activeSlideTitle = null;
        this.tocSlideBeingEdited = null;
        this.tocBusy = false;
        this.references = [];
        const row = this.lessons?.find((l) => Number(l.lessonId) === Number(this.selectedLessonId));
        if (row) row.lessonStatus = 'EMPTY';
        this.loadLessons();
        this.loadReferences();
        this.loadLessonQuizDefaults(this.selectedLessonId!, () => {
          if (this.lessonNeedsReferenceBeforeToc()) {
            this.mode = 'pickLesson';
            this.say(
              'assistant',
              `Lesson content cleared. ${this.referenceRequiredBeforeTocMessage('clear')}`,
            );
            return;
          }
          this.beginAutoTocForEmptyLesson('Lesson content cleared.');
        });
      },
      error: (err: unknown) => {
        this.tocBusy = false;
        const msg = extractApiErrorMessage(err, 'Failed to clear lesson content.');
        this.say('assistant', msg);
      },
    });
  }

  deleteLessonFromChat(): void {
    if (this.isLessonLinkedToProject()) {
      const codes = this.linkedProjectCodesForSelected();
      const list = codes.length ? codes.join(', ') : 'a creator project';
      this.say('assistant', `This lesson is linked to project(s): ${list}. Unlink before deleting the lesson.`);
      return;
    }
    if (
      !window.confirm(
        'Delete this lesson permanently? This removes the lesson, TOC, all slides, project slides, upload folder (images/audio), and references. This cannot be undone.',
      )
    ) {
      this.say('assistant', 'Delete lesson cancelled.');
      return;
    }
    const lessonId = this.selectedLessonId!;
    this.tocBusy = true;
    this.lessonSvc.deleteLesson(this.userId, lessonId).subscribe({
      next: () => {
        this.tocBusy = false;
        this.selectedLessonId = null;
        this.toc = null;
        this.mode = 'pickLesson';
        this.loadLessons();
        this.say('assistant', `Lesson ${lessonId} deleted.`);
      },
      error: (err: unknown) => {
        this.tocBusy = false;
        this.say('assistant', extractApiErrorMessage(err, 'Failed to delete lesson.'));
      },
    });
  }

  private regenerateTocFromChatImpl(): void {
    if (this.hasExpiredOpenAiReferences()) {
      this.say('assistant', this.expiredOpenAiBlockMessage());
      return;
    }
    if (!this.referencesReadyForToc()) {
      this.say('assistant', this.referenceRequiredBeforeTocMessage('generate'));
      return;
    }
    let linkedProjectAction: 'clear' | 'unlink' | undefined;
    if (this.isLessonLinkedToProject()) {
      const action = this.promptClearOrUnlinkLinkedProjects();
      if (!action) {
        this.say('assistant', 'TOC regeneration cancelled.');
        return;
      }
      linkedProjectAction = action;
    }
    const quizConfig = promptQuizConfig(this.savedQuizDefaults);
    if (quizConfig === null) {
      this.say('assistant', 'TOC regeneration cancelled.');
      return;
    }
    this.savedQuizDefaults = quizConfig;
    this.tocBusy = true;
    const actionNote =
      linkedProjectAction === 'clear'
        ? ' Clearing slides in linked project(s)…'
        : linkedProjectAction === 'unlink'
          ? ' Unlinking from project(s)…'
          : '';
    this.say('assistant', `Erasing slides and TOC, then regenerating with AI…${actionNote}`);
    this.tocSvc.eraseAndGenerate(this.userId, this.selectedLessonId!, quizConfig, linkedProjectAction).subscribe({
      next: (res: any) => {
        const data = res?.Data ?? res?.data ?? res;
        if (data) {
          this.toc = this.normalizeTocFromApi(data);
          this.mode = 'toc';
          this.tocDirty = false;
          const summary = data?.tocRegenClearSummary;
          let extra = '';
          if (summary?.linkedProjectAction === 'clear' && summary?.projectCodes?.length) {
            extra = ` Cleared slides/lines in: ${summary.projectCodes.join(', ')}.`;
          } else if (summary?.linkedProjectAction === 'unlink' && summary?.projectCodes?.length) {
            extra = ` Unlinked from: ${summary.projectCodes.join(', ')}.`;
          }
          this.say(
            'assistant',
            `TOC erased and regenerated.${extra} You can reorder sections and slides, then save.`,
          );
        } else {
          this.loadTOC();
        }
        this.tocBusy = false;
        this.loadLessons();
      },
      error: (err) => {
        this.tocBusy = false;
        const msg = extractApiErrorMessage(err, 'TOC regeneration failed.');
        this.handleAiFailure('TOC regeneration failed', msg, () => {
          this.say('assistant', 'Continuing — retry regenerate toc when ready.');
        });
      },
    });
  }

  addSection() {
    if (!this.toc) return;
    this.toc.sections = this.toc.sections || [];
    this.toc.sections.push({
      sectionId: this.uuid(),
      title: 'New Section',
      description: '',
      slides: [],
    });
  }

  moveSectionUp(i: number) {
    if (!this.toc?.sections || i <= 0) return;
    [this.toc.sections[i - 1], this.toc.sections[i]] = [this.toc.sections[i], this.toc.sections[i - 1]];
  }

  moveSectionDown(i: number) {
    if (!this.toc?.sections || i >= this.toc.sections.length - 1) return;
    [this.toc.sections[i + 1], this.toc.sections[i]] = [this.toc.sections[i], this.toc.sections[i + 1]];
  }

  requestDeleteSection(sectionIndex: number) {
    this.confirmDelete = { kind: 'section', sectionIndex };
  }

  addSlide(section: any) {
    section.slides = section.slides || [];
    section.slides.push({
      slideId: this.uuid(),
      title: 'New Slide',
      description: '',
      sourcePageRef: '',
      slideType: '',
      status: 'PENDING',
    });
  }

  moveSlideUp(section: any, i: number) {
    if (!section?.slides || i <= 0) return;
    [section.slides[i - 1], section.slides[i]] = [section.slides[i], section.slides[i - 1]];
  }

  moveSlideDown(section: any, i: number) {
    if (!section?.slides || i >= section.slides.length - 1) return;
    [section.slides[i + 1], section.slides[i]] = [section.slides[i], section.slides[i + 1]];
  }

  requestDeleteSlide(sectionIndex: number, slideIndex: number) {
    this.confirmDelete = { kind: 'slide', sectionIndex, slideIndex };
  }

  confirmDeleteYes() {
    if (!this.toc?.sections || !this.confirmDelete) return;
    const { kind, sectionIndex, slideIndex } = this.confirmDelete;
    if (kind === 'section') {
      this.toc.sections.splice(sectionIndex, 1);
    } else if (kind === 'slide' && typeof slideIndex === 'number') {
      this.toc.sections[sectionIndex].slides.splice(slideIndex, 1);
    }
    this.confirmDelete = null;
  }

  confirmDeleteNo() {
    this.confirmDelete = null;
  }

  // Slide layout (slideType): set in creator (project slides) only, or derived automatically later — not edited in SME chat UI; we still round-trip slideType for API/PPT compatibility.
  // ---------- Slide ----------
  isQuizSlide(slide: { contentType?: string; slideType?: string }): boolean {
    return isQuizTocSlide(slide);
  }

  openSlide(section: any, slide: any) {
    if (!this.selectedLessonId) return;
    if (this.slideBusy) {
      this.say('assistant', 'Slide generation is already in progress — please wait.');
      return;
    }
    if (this.tocBusy) {
      this.say('assistant', 'TOC is loading — please wait before opening a slide.');
      return;
    }
    if (this.hasExpiredOpenAiReferences()) {
      this.say('assistant', this.expiredOpenAiBlockMessage());
      return;
    }
    if (!this.maybeSaveBeforeNavigate('slide')) return;

    let allowMissingSourcePageRef = false;
    if (documentStrictSlideNeedsPageRef(this.toc?.lessonContentMode, slide)) {
      if (!window.confirm(documentStrictMissingPageRefMessage(slide.title))) {
        return;
      }
      allowMissingSourcePageRef = true;
    }

    this.activeSlideId = slide.slideId;
    this.activeSlideTitle = slide.title;
    this.lastTocSlideId = slide.slideId;
    this.highlightedTocSlideId = slide.slideId;
    this.tocSlideBeingEdited = slide;
    this.generatingSlideId = slide.slideId;
    this.generatingSlideTitle = slide.title || '';
    this.generatingProgressMessage = 'Starting…';
    this.say('assistant', `Generating/loading slide ${slide.title} (auto-generates if missing)...`);
    this.slideBusy = true;
    void this.slideSvc
      .streamGetLessonSlide(
        this.userId,
        this.selectedLessonId,
        slide.slideId,
        slide.title,
        (ev) => {
        if (ev.type === 'progress' && ev.message) {
          this.generatingProgressMessage = String(ev.message);
        }
        if (ev.type === 'warning' && ev.message) {
          this.say('assistant', 'Note: ' + String(ev.message));
        }
        if (ev.type === 'complete' && ev['slide']) {
          const s = ev['slide'] as any;
          const warnList = (ev['warnings'] as string[] | undefined) || s.warnings || [];
          const inserted = (ev['tocSlidesInserted'] as any[] | undefined) || s.tocSlidesInserted || [];
          const merged = mergeInsertedTocSlides(this.toc, slide.slideId, inserted);
          if (merged > 0) {
            this.tocDirty = true;
            this.say(
              'assistant',
              `${merged} figure slide(s) were added to the TOC (large images split onto their own slides).`,
            );
          }
          this.activeSlide = s;
          this.imageLoadState = {};
          this.lessonImgCandidateIdx = {};
          this.slideDirty = false;
          const fromLesson = s.slideType != null && String(s.slideType).trim() !== '' ? String(s.slideType).trim() : '';
          const fromToc = slide.slideType != null && String(slide.slideType).trim() !== '' ? String(slide.slideType).trim() : '';
          this.activeSlideEdit = {
            slideTitle: s.slideTitle,
            openingNote: s.openingNote,
            closingNote: s.closingNote,
            slideType: fromLesson || fromToc,
            slideElements: Array.isArray(s.elements) ? s.elements.map((el: any) => ({ ...el })) : [],
          };
          this.syncPreviewLayoutImgCandidates();
          this.mode = 'slide';
          this.say('assistant', 'Slide loaded. Edit and save when ready.');
          for (const w of warnList) {
            this.say('assistant', 'Note: ' + w);
          }
          slide.status = 'FILLED';
        }
        if (ev.type === 'error') {
          const msg = String(ev.message || 'Slide load failed.');
          this.handleAiFailure('Slide load failed', msg, () => this.backToTOC());
        }
      },
        { allowMissingSourcePageRef },
      )
      .catch((e: Error) => {
        const msg = e?.message || 'Slide load failed.';
        this.handleAiFailure('Slide load failed', msg, () => this.backToTOC());
      })
      .finally(() => {
        this.slideBusy = false;
        this.generatingSlideId = null;
        this.generatingSlideTitle = '';
        this.generatingProgressMessage = '';
      });
  }

  backToTOC() {
    if (this.slideBusy) {
      this.say('assistant', 'Slide generation is in progress — please wait before returning to TOC.');
      return;
    }
    if (!this.maybeSaveBeforeNavigate('toc')) return;
    const returnSlideId = this.activeSlideId || this.lastTocSlideId;
    this.mode = 'toc';
    this.activeSlide = null;
    this.activeSlideEdit = null;
    this.activeSlideId = null;
    this.activeSlideTitle = null;
    this.tocSlideBeingEdited = null;
    this.imageLoadState = {};
    this.lessonImgCandidateIdx = {};
    this.previewLayoutImgCandidates = [];
    this.previewLayoutImgIdx = 0;
    this.showSlideNotes = false;
    this.showElementNotes = false;
    this.previewExpanded = false;
    this.showAdvancedElementFields = false;
    if (returnSlideId) {
      this.lastTocSlideId = returnSlideId;
      this.highlightedTocSlideId = returnSlideId;
      this.scheduleFocusTocSlideRow(returnSlideId);
    }
  }

  private scheduleFocusTocSlideRow(slideId: string | null | undefined): void {
    const id = String(slideId || '').trim();
    if (!id) return;
    // Defer until Angular renders the TOC panel after mode === 'toc'.
    setTimeout(() => this.focusTocSlideRow(id), 0);
  }

  private focusTocSlideRow(slideId: string | null | undefined): void {
    scrollToTocSlideWhenReady(slideId, this.colDetails?.nativeElement, {
      maxAttempts: 30,
      intervalMs: 50,
    });
  }

  toggleBullet(el: any) {
    const cur = String(el?.bulletFlag ?? 'N').toUpperCase();
    el.bulletFlag = cur === 'Y' ? 'N' : 'Y';
    this.slideDirty = true;
  }

  indentDelta(el: any, delta: number) {
    const cur = Number(el?.indentLevel ?? 0);
    const next = Math.max(0, cur + delta);
    el.indentLevel = next;
    this.slideDirty = true;
  }

  autoGrow1(event: any) {
    const el = event?.target as HTMLTextAreaElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }

  onImgLoad(el: any) {
    const id = String(el?.elementId ?? '');
    if (!id) return;
    this.imageLoadState[id] = 'ok';
  }

  onImgError(el: any) {
    const id = String(el?.elementId ?? '');
    if (!id || this.selectedLessonId == null || !this.activeSlideId) return;
    const candidates = lessonImageContentUrlCandidates(this.userId, this.selectedLessonId, this.activeSlideId, el?.content);
    const cur = this.lessonImgCandidateIdx[id] ?? 0;
    if (cur + 1 < candidates.length) {
      this.lessonImgCandidateIdx[id] = cur + 1;
      delete this.imageLoadState[id];
      return;
    }
    this.imageLoadState[id] = 'error';
  }

  onSlideElementContentChange(el: any) {
    this.slideDirty = true;
    if (this.normalizedElementType(el) !== 'IMAGE') return;
    const id = String(el?.elementId ?? '');
    if (id) {
      delete this.lessonImgCandidateIdx[id];
      delete this.imageLoadState[id];
    }
    this.syncPreviewLayoutImgCandidates();
  }

  onSlideElementTypeChange(el: any) {
    this.slideDirty = true;
    const id = String(el?.elementId ?? '');
    if (id) {
      delete this.lessonImgCandidateIdx[id];
      delete this.imageLoadState[id];
    }
    this.syncPreviewLayoutImgCandidates();
  }

  private syncPreviewLayoutImgCandidates(): void {
    if (!this.activeSlideEdit?.slideElements || this.selectedLessonId == null || !this.activeSlideId) {
      this.previewLayoutImgCandidates = [];
      this.previewLayoutImgIdx = 0;
      return;
    }
    const firstImg = this.activeSlideEdit.slideElements.find(
      (e: any) => this.normalizedElementType(e) === 'IMAGE' && String(e?.content || '').trim()
    );
    if (!firstImg) {
      this.previewLayoutImgCandidates = [];
      this.previewLayoutImgIdx = 0;
      return;
    }
    this.previewLayoutImgCandidates = lessonImageContentUrlCandidates(
      this.userId,
      this.selectedLessonId,
      this.activeSlideId,
      firstImg.content
    );
    this.previewLayoutImgIdx = 0;
  }

  onPreviewLayoutImageError(ev: Event): void {
    const list = this.previewLayoutImgCandidates;
    const img = ev.target as HTMLImageElement;
    this.previewLayoutImgIdx += 1;
    if (this.previewLayoutImgIdx < list.length) {
      img.src = list[this.previewLayoutImgIdx];
    }
  }

  addSlideElement() {
    if (!this.activeSlideEdit) return;
    this.activeSlideEdit.slideElements = this.activeSlideEdit.slideElements || [];
    this.activeSlideEdit.slideElements.push({
      elementId: this.uuid(),
      elementType: 'TEXT',
      bulletFlag: 'N',
      indentLevel: 0,
      imageCaption: '',
      content: '',
      notes: '',
      imagePrompt: null,
    });
    this.slideDirty = true;
    this.syncPreviewLayoutImgCandidates();
  }

  insertSlideElementAfter(i: number) {
    if (!this.activeSlideEdit) return;
    this.activeSlideEdit.slideElements = this.activeSlideEdit.slideElements || [];
    this.activeSlideEdit.slideElements.splice(i + 1, 0, {
      elementId: this.uuid(),
      elementType: 'TEXT',
      bulletFlag: 'N',
      indentLevel: 0,
      imageCaption: '',
      content: '',
      notes: '',
      imagePrompt: null,
    });
    this.slideDirty = true;
    this.syncPreviewLayoutImgCandidates();
  }

  deleteSlideElement(i: number) {
    this.confirmDeleteElementIndex = i;
  }

  confirmDeleteElementYes() {
    const i = this.confirmDeleteElementIndex;
    if (i === null) return;
    if (!this.activeSlideEdit?.slideElements) return;
    this.activeSlideEdit.slideElements.splice(i, 1);
    this.confirmDeleteElementIndex = null;
    this.slideDirty = true;
    this.syncPreviewLayoutImgCandidates();
  }

  confirmDeleteElementNo() {
    this.confirmDeleteElementIndex = null;
  }

  moveSlideElementUp(i: number) {
    const arr = this.activeSlideEdit?.slideElements;
    if (!arr || i <= 0) return;
    [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
    this.slideDirty = true;
    this.syncPreviewLayoutImgCandidates();
  }

  moveSlideElementDown(i: number) {
    const arr = this.activeSlideEdit?.slideElements;
    if (!arr || i >= arr.length - 1) return;
    [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]];
    this.slideDirty = true;
    this.syncPreviewLayoutImgCandidates();
  }

  saveSlideFromChat() {
    if (!this.selectedLessonId || !this.activeSlideId || !this.activeSlideEdit) return;
    this.slideSvc.saveLessonSlide(this.userId, this.selectedLessonId, this.activeSlideId, this.activeSlideEdit).subscribe({
      next: () => {
        this.slideDirty = false;
        this.say('assistant', 'Slide saved.');
        if (this.tocSlideBeingEdited) {
          this.tocSlideBeingEdited.slideType = this.activeSlideEdit.slideType;
        }
        // Keep preview in sync with edited values
        this.activeSlide = {
          ...this.activeSlide,
          slideTitle: this.activeSlideEdit.slideTitle,
          openingNote: this.activeSlideEdit.openingNote,
          closingNote: this.activeSlideEdit.closingNote,
          slideType: this.activeSlideEdit.slideType,
          elements: this.activeSlideEdit.slideElements,
        };
        this.syncPreviewLayoutImgCandidates();
      },
      error: () => this.say('assistant', 'Failed to save slide.'),
    });
  }

  // Warn user about unsaved changes; simple confirm for now.
  private maybeSaveBeforeNavigate(target: 'lesson' | 'slide' | 'toc'): boolean {
    if (this.aiGenerationBusy) {
      const what = this.tocBusy
        ? 'TOC generation'
        : this.slideBusy
          ? 'Slide generation'
          : 'AI lesson generation';
      window.alert(`${what} is in progress. Please wait until it completes.`);
      return false;
    }
    if (this.slideDirty) {
      const ok = window.confirm('You have unsaved slide changes. Save now?');
      if (ok) {
        this.saveSlideFromChat();
      }
      // Even if they choose not to save, allow navigation.
      this.slideDirty = false;
    }
    if (this.tocDirty && target !== 'slide') {
      const ok = window.confirm('You have unsaved TOC changes. Save now?');
      if (ok) {
        this.saveTOC();
      }
      this.tocDirty = false;
    }
    return true;
  }

  normalizedElementType(el: any): string {
    return String(el?.elementType ?? '')
      .trim()
      .toUpperCase();
  }

  imageUrlForElement(el: any): string | null {
    if (!this.activeSlideId || this.selectedLessonId == null) return null;
    const t = this.normalizedElementType(el);
    if (!el || t !== 'IMAGE') return null;
    const candidates = lessonImageContentUrlCandidates(this.userId, this.selectedLessonId, this.activeSlideId, el.content);
    if (!candidates.length) return null;
    const id = String(el.elementId ?? '');
    const idx = Math.min(this.lessonImgCandidateIdx[id] ?? 0, candidates.length - 1);
    let url = candidates[idx];
    const bust = this.imagePreviewBust[id];
    if (bust) {
      url += (url.includes('?') ? '&' : '?') + 't=' + bust;
    }
    return url;
  }

  /** Prompt used for IMAGE regen: imagePrompt → notes → non-filename content. */
  resolveImagePromptForElement(el: any): string {
    const fromPrompt = String(el?.imagePrompt ?? '').trim();
    if (fromPrompt) return fromPrompt;
    const fromNotes = String(el?.notes ?? '').trim();
    if (fromNotes) return fromNotes;
    const content = String(el?.content ?? '').trim();
    if (!content) return '';
    if (/\.(png|jpe?g|webp|gif)$/i.test(content)) return '';
    if (content.includes('/') || content.includes('\\')) return '';
    return content;
  }

  regenerateSlideImage(el: any): void {
    if (!el || this.normalizedElementType(el) !== 'IMAGE') return;
    if (this.selectedLessonId == null || !this.activeSlideId) {
      this.say('assistant', 'Open a slide first.');
      return;
    }
    if (this.remoteWorkBusy || this.regeneratingImageElementId) {
      window.alert('Another generation is in progress. Please wait.');
      return;
    }
    const prompt = this.resolveImagePromptForElement(el);
    if (!prompt) {
      window.alert(
        'No image prompt found. Put the prompt in Notes (or imagePrompt), then try again.',
      );
      return;
    }
    const elementId = String(el.elementId || '').trim();
    this.regeneratingImageElementId = elementId || '__img__';
    this.say('assistant', 'Regenerating image…');
    this.slideSvc
      .regenerateSlideImage(this.userId, this.selectedLessonId, this.activeSlideId, {
        elementId: elementId || undefined,
        prompt,
      })
      .pipe(finalize(() => {
        this.regeneratingImageElementId = null;
      }))
      .subscribe({
        next: (res) => {
          const data = (unwrapVioResponseData(res) ?? res) as any;
          const filename = String(data?.filename || data?.content || '').trim();
          if (!filename) {
            this.say('assistant', 'Image regeneration returned no filename.');
            return;
          }
          el.content = filename;
          el.imagePrompt = String(data?.imagePrompt || prompt).trim() || prompt;
          const id = String(el.elementId ?? '');
          if (id) {
            delete this.lessonImgCandidateIdx[id];
            delete this.imageLoadState[id];
            this.imagePreviewBust[id] = Date.now();
          }
          this.syncPreviewLayoutImgCandidates();
          this.say('assistant', `Image regenerated: ${filename}`);
        },
        error: (err) => {
          const msg = extractApiErrorMessage(err, 'Image regeneration failed.');
          this.say('assistant', msg);
        },
      });
  }

  // Frontend layout preview helpers
  previewIndentPx(indent: number): number {
    const safe = Math.max(0, Math.min(Number(indent ?? 0), 6));
    return safe * 12;
  }

  getPreviewImageUrl(): string | null {
    const list = this.previewLayoutImgCandidates;
    if (!list.length) return null;
    const i = Math.min(this.previewLayoutImgIdx, list.length - 1);
    return list[i];
  }

  getPreviewTextLines(): Array<{ text: string; bullet: boolean; indent: number }> {
    const els = this.activeSlideEdit?.slideElements || [];
    return els
      .filter((e: any) => this.normalizedElementType(e) === 'TEXT')
      .map((e: any) => ({
        text: String(e?.content || ''),
        bullet: String(e?.bulletFlag || 'N').toUpperCase() === 'Y',
        indent: Number(e?.indentLevel ?? 0),
      }))
      .filter((x: { text: string; bullet: boolean; indent: number }) => x.text.trim().length > 0);
  }

  // ---------- Utils ----------
  uuid(): string {
    return 'xxxxxx'.replace(/x/g, () => Math.floor(Math.random() * 16).toString(16));
  }

  /** Strip leading "no", "number", "#" from a slide ref (chat). */
  normalizeSlideRef(ref: string): string {
    return ref.replace(/^(?:no\.?|nr\.?|number|#)\s*/i, '').trim();
  }

  private flattenTocSlides(): Array<{ section: any; slide: any }> {
    const out: Array<{ section: any; slide: any }> = [];
    for (const s of this.toc?.sections || []) {
      for (const sl of s.slides || []) {
        out.push({ section: s, slide: sl });
      }
    }
    return out;
  }

  /** Resolve by 1-based TOC order or by slideId (case-insensitive id match). */
  private resolveTocSlideRef(ref: string): { section: any; slide: any } | null {
    const r = this.normalizeSlideRef(ref);
    if (!r) return null;
    if (/^\d+$/.test(r)) {
      const flat = this.flattenTocSlides();
      const n = Number(r);
      const item = flat[n - 1];
      return item ? { section: item.section, slide: item.slide } : null;
    }
    for (const sec of this.toc?.sections || []) {
      const sl = (sec.slides || []).find(
        (x: any) => String(x.slideId).toLowerCase() === r.toLowerCase()
      );
      if (sl) return { section: sec, slide: sl };
    }
    return null;
  }

  /** Move first slide after second (TOC order or id); cross-section allowed. */
  private moveSlideAfterInToc(fromRef: string, toRef: string): boolean {
    const a = this.resolveTocSlideRef(fromRef);
    const b = this.resolveTocSlideRef(toRef);
    if (!a || !b) {
      this.say(
        'assistant',
        'Could not find one or both slides. Use numbers in TOC order (1 = first slide) or slide ids (e.g. SL_…).'
      );
      return false;
    }
    if (a.slide === b.slide) {
      this.say('assistant', 'Choose two different slides.');
      return false;
    }
    const fromSec = a.section;
    const toSec = b.section;
    const slidesFrom = fromSec.slides || [];
    const slidesTo = toSec.slides || [];
    let fromIdx = slidesFrom.indexOf(a.slide);
    let toIdx = slidesTo.indexOf(b.slide);
    if (fromIdx < 0 || toIdx < 0) return false;
    const [moved] = slidesFrom.splice(fromIdx, 1);
    if (fromSec === toSec && fromIdx < toIdx) {
      toIdx -= 1;
    }
    slidesTo.splice(toIdx + 1, 0, moved);
    this.tocDirty = true;
    return true;
  }

  onOpenLessonLifecycleChange(status: string) {
    if (!this.selectedLessonId) return;
    const st = String(status || '').toUpperCase();
    if (!['IN_PROGRESS', 'RELEASED', 'ARCHIVED'].includes(st)) return;
    this.lessonSvc.updateLessonLifecycle(this.userId, this.selectedLessonId, st).subscribe({
      next: () => {
        this.selectedLessonLifecycleStatus = st as any;
        const row = this.lessons?.find((l) => l.lessonId === this.selectedLessonId);
        if (row) row.lessonLifecycleStatus = st;
        this.say('assistant', `Lesson workflow set to ${st}.`);
      },
      error: (err) => {
        const msg =
          err?.error?.error ||
          err?.error?.errorDescription ||
          err?.message ||
          'Failed';
        this.say('assistant', 'Could not update workflow: ' + msg);
      },
    });
  }

  generatePptFromChat(lessonId?: number) {
    const id = lessonId != null && Number.isFinite(lessonId) ? lessonId : this.selectedLessonId;
    if (id == null) {
      this.say('assistant', 'Open a lesson first, or say e.g. generate ppt for lesson 5.');
      return;
    }
    this.pptGenBusy = true;
    this.lessonSvc
      .generatePPT(this.userId, id, 'REVIEW')
      .pipe(finalize(() => (this.pptGenBusy = false)))
      .subscribe({
        next: (data: any) => {
          triggerGeneratedLessonPptDownload(
            this.commonAPI,
            `${environment.baseUrl}/lessonMgmt`,
            this.userId,
            id,
            data,
            (ok: boolean, message: string) => this.say('assistant', message),
          );
        },
        error: (err) => {
          const msg =
            err?.error?.error ||
            err?.error?.errorDescription ||
            err?.message ||
            'Failed';
          this.say('assistant', 'PPT generation failed: ' + msg);
        },
      });
  }
}

